import React, { useEffect, useRef, useState, useCallback } from 'react';
import { XMarkIcon, ShareIcon, PhotoIcon, ClipboardIcon, CheckIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { SHARE_BACKGROUNDS, BACKGROUND_CATEGORIES } from '../utils/shareCardBackgrounds';
import { renderShareCard, shareCard, downloadCard, buildShareCaption, CARD_SIZES } from '../utils/shareCard';

// Small canvas thumbnail for one background template.
function BackgroundThumb({ background, selected, onSelect }) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) background.draw(ref.current.getContext('2d'), 108, 108);
  }, [background]);
  return (
    <button
      type="button"
      onClick={onSelect}
      title={background.name}
      className={`flex-shrink-0 rounded-xl overflow-hidden transition-all ${
        selected ? 'ring-2 ring-cerulean ring-offset-2 dark:ring-offset-[#0d1520]' : 'opacity-90 hover:opacity-100'
      }`}
    >
      <canvas ref={ref} width={108} height={108} className="w-14 h-14 block" />
    </button>
  );
}

// Full-screen composer: pick a verse, a background (template or photo), a
// format, preview the card live, then share via the native sheet.
export default function ShareCardComposer({ open, onClose, verses = [], bookName, chapter }) {
  const [verseIdx, setVerseIdx] = useState(0);
  const [category, setCategory] = useState('nature');
  const [backgroundId, setBackgroundId] = useState(SHARE_BACKGROUNDS[0].id);
  const [photo, setPhoto] = useState(null); // HTMLImageElement
  const [size, setSize] = useState('square');
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [notice, setNotice] = useState('');
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);

  const verse = verses[verseIdx] || verses[0];
  const verseRef = verse ? `${bookName} ${chapter}:${verse.verseNumber}` : '';
  const background = SHARE_BACKGROUNDS.find(b => b.id === backgroundId) || SHARE_BACKGROUNDS[0];

  // Reset per open
  useEffect(() => {
    if (!open) return;
    setVerseIdx(0);
    setPhoto(null);
    setCopied(false);
    setNotice('');
  }, [open]);

  // Live preview render
  useEffect(() => {
    if (!open || !canvasRef.current || !verse) return;
    renderShareCard(canvasRef.current, {
      background: photo ? null : background,
      photo,
      verseText: verse.text,
      verseRef,
      size,
    });
  }, [open, verse, verseRef, background, photo, size]);

  const handlePhotoChosen = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => setPhoto(img);
    img.onerror = () => setNotice('Could not load that photo — try a different one.');
    img.src = url;
    e.target.value = '';
  }, []);

  async function handleShare() {
    if (!canvasRef.current) return;
    setBusy(true);
    setNotice('');
    try {
      const result = await shareCard(canvasRef.current, verseRef);
      if (result === 'downloaded') {
        setNotice('Image downloaded — attach it to your post, and the caption is one tap away below.');
      }
    } catch (e) {
      console.error('ShareCardComposer: share failed', e);
      setNotice('Could not share the image. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  async function handleCopyCaption() {
    const caption = buildShareCaption(verseRef);
    try {
      // navigator.clipboard is undefined on insecure origins (e.g. testing
      // over LAN http://192.168…) — fall back to a hidden textarea copy.
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(caption);
      } else {
        const ta = document.createElement('textarea');
        ta.value = caption;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        ta.remove();
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      console.error('ShareCardComposer: copy failed', e);
      setNotice(`Could not copy automatically. Caption: ${caption}`);
    }
  }

  if (!open || !verse) return null;

  const categoryBackgrounds = SHARE_BACKGROUNDS.filter(b => b.category === category);
  const { width: cw, height: ch } = CARD_SIZES[size];

  return (
    <div className="fixed inset-0 z-[10000] bg-black/70 flex items-end sm:items-center justify-center">
      <div className="bg-white dark:bg-[#0d1520] w-full sm:max-w-lg sm:rounded-2xl rounded-t-2xl max-h-[94vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-white/10 flex-shrink-0">
          <h2 className="font-semibold text-gray-900 dark:text-white">Share the Moment</h2>
          <button type="button" onClick={onClose} className="p-1 rounded-lg text-gray-400 hover:bg-gray-100 dark:hover:bg-white/10">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
          {/* Preview */}
          <div className="flex justify-center">
            <canvas
              ref={canvasRef}
              className="rounded-xl shadow-lg max-w-full"
              style={{ width: size === 'story' ? 210 : 300, height: (size === 'story' ? 210 : 300) * (ch / cw) }}
            />
          </div>

          {/* Format toggle */}
          <div className="flex justify-center gap-1.5">
            {Object.entries(CARD_SIZES).map(([key, s]) => (
              <button
                key={key}
                type="button"
                onClick={() => setSize(key)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                  size === key
                    ? 'bg-cerulean text-white'
                    : 'bg-gray-100 dark:bg-white/10 text-gray-600 dark:text-white/60'
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>

          {/* Verse picker */}
          {verses.length > 1 && (
            <div>
              <p className="text-xs font-semibold text-gray-500 dark:text-white/40 uppercase tracking-wider mb-1.5">Verse</p>
              <div className="flex gap-1.5 overflow-x-auto pb-1" style={{ scrollbarWidth: 'none' }}>
                {verses.map((v, i) => (
                  <button
                    key={v.verseNumber}
                    type="button"
                    onClick={() => setVerseIdx(i)}
                    className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                      i === verseIdx
                        ? 'bg-cerulean text-white'
                        : 'bg-gray-100 dark:bg-white/10 text-gray-600 dark:text-white/60'
                    }`}
                  >
                    v. {v.verseNumber}
                  </button>
                ))}
              </div>
              <p className="text-xs text-gray-400 dark:text-white/35 mt-1.5 line-clamp-2">{verse.text}</p>
            </div>
          )}

          {/* Background picker */}
          <div>
            <div className="flex items-center gap-1.5 mb-2 overflow-x-auto" style={{ scrollbarWidth: 'none' }}>
              {BACKGROUND_CATEGORIES.map(cat => (
                <button
                  key={cat.key}
                  type="button"
                  onClick={() => { setCategory(cat.key); setPhoto(null); }}
                  className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                    category === cat.key && !photo
                      ? 'bg-cerulean text-white'
                      : 'bg-gray-100 dark:bg-white/10 text-gray-600 dark:text-white/60'
                  }`}
                >
                  {cat.label}
                </button>
              ))}
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors inline-flex items-center gap-1 ${
                  photo ? 'bg-cerulean text-white' : 'bg-gray-100 dark:bg-white/10 text-gray-600 dark:text-white/60'
                }`}
              >
                <PhotoIcon className="w-3.5 h-3.5" /> My Photo
              </button>
              <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handlePhotoChosen} />
            </div>
            {!photo && (
              <div className="flex gap-2 overflow-x-auto pb-1" style={{ scrollbarWidth: 'none' }}>
                {categoryBackgrounds.map(bg => (
                  <BackgroundThumb
                    key={bg.id}
                    background={bg}
                    selected={bg.id === backgroundId}
                    onSelect={() => setBackgroundId(bg.id)}
                  />
                ))}
              </div>
            )}
            {photo && (
              <p className="text-xs text-gray-400 dark:text-white/35">
                Using your photo. Tap a category above to go back to templates.
              </p>
            )}
          </div>

          {notice && (
            <p className="text-xs text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-400/10 rounded-lg px-3 py-2">{notice}</p>
          )}
        </div>

        {/* Footer actions */}
        <div className="flex-shrink-0 px-4 py-3 border-t border-gray-200 dark:border-white/10 flex gap-2">
          <button
            type="button"
            onClick={handleCopyCaption}
            className="btn-secondary flex items-center justify-center gap-2 px-4"
          >
            {copied ? <CheckIcon className="w-4 h-4 text-green-500" /> : <ClipboardIcon className="w-4 h-4" />}
            {copied ? 'Copied' : 'Caption'}
          </button>
          <button
            type="button"
            onClick={async () => {
              try {
                await downloadCard(canvasRef.current, verseRef);
              } catch (e) {
                console.error('ShareCardComposer: save failed', e);
                setNotice('Could not save the image. Please try again.');
              }
            }}
            className="btn-secondary flex items-center justify-center gap-2 px-4"
            title="Save image"
          >
            <ArrowDownTrayIcon className="w-4 h-4" /> Save
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={handleShare}
            className="btn-primary flex-1 flex items-center justify-center gap-2"
          >
            <ShareIcon className="w-4 h-4" /> {busy ? 'Preparing…' : 'Share'}
          </button>
        </div>
      </div>
    </div>
  );
}
